import { getUsersByIdsOptions, ParticipantsForSurveyQueryOptions } from "@pulse/api/queryApi";
import type { User } from "@pulse/api/userAPI";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function ParticipantsList({ surveyId }: { surveyId: number }) {
    const { t } = useTranslation();
    const { data: participants } = useQuery(ParticipantsForSurveyQueryOptions({ surveyId }));

    const userIds = useMemo(() => {
        return participants?.filter((p) => p.userId).map((p) => p.userId) as number[];
    }, [participants]);

    const { data: users } = useQuery(
        getUsersByIdsOptions({
            ids: userIds || [],
        }),
    );

    const usersMap = useMemo(() => {
        const map = new Map<number, User>();
        users?.forEach((user) => {
            map.set(user.id, user);
        });
        return map;
    }, [users]);

    const participantLabels = useMemo(() => {
        return participants
            ?.map((p) => {
                if (p.userId) {
                    const user = usersMap.get(p.userId);
                    return user ? `${user.firstName} ${user.lastName}` : t("common.unknownUser");
                }
                return p.email || t("common.unknownUser");
            })
            .sort((a, b) => a.localeCompare(b));
    }, [participants, usersMap, t]);

    return (
        <div className="max-h-98 w-80 overflow-auto pr-4">
            <ol className="flex flex-col gap-2">
                {participantLabels?.map((label, idx) => {
                    return (
                        <li
                            key={idx}
                            className="p-2 border bg-secondary/50 border-secondary-outline rounded-sm"
                        >
                            {label}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
